import request from "../utils/request";
import { normalizePaginationInteger, type PaginationIntegerValue } from "./pagination";

/**
 * admin/users 页面对应的后端 API 封装。
 * 后端路由前缀：/api/_Admin/UserManagement
 */

// ────────────── 领域类型 ──────────────

export type UserStatus = "Active" | "Disabled";
/** 用户所属区域。决定可选角色范围与 General Source Type 的默认锁定分类 */
export type UserRegion = "Mainland" | "HK";

/** 各区域下默认选中且不可取消的分类名 */
export const REGION_LOCKED_CATEGORY: Record<UserRegion, string> = {
  Mainland: "General Docs",
  HK: "HK General Docs",
};

/**
 * 全部区域的锁定分类。这些分类由区域自动决定归属，
 * 一律不出现在 General Source Type 下拉里——
 * 自己区域的那个已默认选中且不可取消，别的区域的则不该授予。
 */
export const REGION_LOCKED_CATEGORIES: ReadonlySet<string> = new Set(
  Object.values(REGION_LOCKED_CATEGORY),
);

/** HK 用户仅允许该角色 */
export const HK_ONLY_ROLE = "Staff";

export type UserSource = "sso" | "manual" | "dg" | null;
/** 用户来源筛选/标记的合法值：sso = SSO 登录，manual = 手动添加，dg = 通讯组同步 */
export type UserSourceValue = NonNullable<UserSource>;
/** 角色为后端 FrameworkRole 动态数据，前端不再做枚举 */
export type UserRole = string;

/**
 * 用户管理页使用的用户模型（由后端 UserManagement/Search 适配而来）。
 * 角色统一为单一 roles 列表——后端不区分内部/外部角色。
 */
export interface User {
  id: string;
  name: string;
  email: string;
  kAccount?: string;
  /** 用户主来源：sso = SSO 登录，manual = 手动添加（等于 sources[0]） */
  source: UserSource;
  /** 用户全部来源标记（"sso" | "manual" | "dg"，可能多个） */
  sources: string[];
  /** 全部角色，来源于后端，不区分内部/外部 */
  roles: string[];
  status: UserStatus;
  /** General Source Type 权限：可访问的文档分类 ID（非名称） */
  docCategoryIds: string[];
  /** 当前用户可访问的 MVR Study Code（由 Study Source 成员权限解析） */
  studyCodes: string[];
  /** 所属区域 */
  region: UserRegion;
  /** 已格式化的最近活跃时间，无则为 "-" */
  lastActive: string;
  /** 已格式化的创建日期 */
  createdAt: string;
}

// ────────────── 请求/响应类型 ──────────────

export interface UserSearchParams {
  Keyword?: string;
  /** "All" | "Active" | "Disabled" */
  Status?: "All" | UserStatus;
  /** "All" | "sso" | "manual" | "dg" */
  Source?: "All" | UserSourceValue;
  /** "All" 或具体角色名（如 "Admin"） */
  Role?: "All" | UserRole;
  /** "All" | "Mainland" | "HK" */
  Region?: "All" | UserRegion;
  /** "All" 或 DocCategory.ID */
  GeneralSourceTypeId?: string;
  Page?: number;
  Limit?: number;
}

export interface UserItem {
  Id: string;
  ITCode: string;
  Name: string;
  Email: string;
  KAccount?: string | null;
  Source: UserSource;
  /** 用户全部来源标记（"sso" | "manual" | "dg"，可能多个）；Source 保留并等于 Sources[0] */
  Sources?: string[];
  Status: UserStatus;
  /** 用户全部角色（来自 FrameworkUserRole），不区分内部/外部 */
  Roles?: string[];
  /** General Source Type 权限：可访问的文档分类 ID */
  DocCategoryIds?: string[];
  /** 可访问的 MVR Study Code */
  StudyCodes?: string[];
  /** 所属区域 */
  Region?: UserRegion;
  /** UTC ISO 字符串，可能为空 */
  LastActive: string;
  /** UTC ISO 字符串 */
  CreatedAt: string;
}

type UserItemWire = Partial<UserItem> & {
  id?: string;
  itCode?: string;
  name?: string;
  email?: string;
  kAccount?: string | null;
  source?: UserSource;
  sources?: string[];
  status?: UserStatus;
  roles?: string[];
  docCategoryIds?: string[];
  DocCategoryIds?: string[];
  studyCodes?: string[];
  StudyCodes?: string[];
  region?: UserRegion;
  Region?: UserRegion;
  lastActive?: string;
  createdAt?: string;
};

export interface UserSearchResponse {
  Count: number;
  Page: number;
  Limit: number;
  Data: UserItemWire[];
  count?: number;
  page?: number;
  limit?: number;
  data?: UserItemWire[];
}

interface UserSearchWire {
  Count?: PaginationIntegerValue;
  Page?: PaginationIntegerValue;
  Limit?: PaginationIntegerValue;
  Data?: UserItemWire[];
  count?: PaginationIntegerValue;
  page?: PaginationIntegerValue;
  limit?: PaginationIntegerValue;
  data?: UserItemWire[];
}

export interface UserRoleOption {
  Code: string;
  Name: string;
  Remark?: string | null;
}

export interface UserCreateRequest {
  Name?: string;
  Email: string;
  Roles: string[];
  Status?: UserStatus;
  /** General Source Type 权限：可访问的文档分类 ID */
  DocCategoryIds?: string[];
  /** 所属区域。必填 */
  Region: UserRegion;
}

export interface UserEditRequest {
  Id: string;
  Name?: string;
  Email?: string;
  /** null/undefined = 不变；空数组 = 清空 */
  Roles?: string[];
  Status?: UserStatus;
  /** General Source Type 权限：undefined = 不变；空数组 = 清空 */
  DocCategoryIds?: string[];
  /** 所属区域：undefined = 不变 */
  Region?: UserRegion;
}

// ────────────── API ──────────────

const BASE = "/_Admin/UserManagement";

export async function searchUsers(params: UserSearchParams) {
  const response = await request.post<UserSearchWire>(`${BASE}/Search`, params);
  const body = response.data ?? {};
  const data: UserSearchResponse = {
    Count: normalizePaginationInteger(body.Count ?? body.count, 0, 0),
    Page: normalizePaginationInteger(body.Page ?? body.page, params.Page ?? 1, 1),
    Limit: normalizePaginationInteger(body.Limit ?? body.limit, params.Limit ?? 20, 1),
    Data: body.Data ?? body.data ?? [],
  };
  return { ...response, data };
}

export function listRoles() {
  return request.get<UserRoleOption[]>(`${BASE}/Roles`);
}

export function createUser(body: UserCreateRequest) {
  return request.post<{ Id: string; ITCode: string }>(`${BASE}/Add`, body);
}

export function updateUser(body: UserEditRequest) {
  return request.put<{ Id: string }>(`${BASE}/Edit`, body);
}

export function toggleUserStatus(id: string) {
  return request.post<{ Id: string; Status: UserStatus }>(`${BASE}/ToggleStatus`, { Id: id });
}

export function deleteUsers(ids: string[]) {
  return request.post<{ Deleted: number }>(`${BASE}/Delete`, { Ids: ids });
}

// ────────────── 后端 -> 前端模型适配 ──────────────

/**
 * 把后端返回的 UserItem 适配为页面使用的 User 结构。
 * 后端不区分内部/外部角色，统一返回单一 Roles。
 */
export function toUser(u: UserItemWire): User {
  const id = u.Id ?? u.id ?? "";
  const name = u.Name ?? u.name ?? "";
  const email = u.Email ?? u.email ?? "";
  const kAccount = u.KAccount ?? u.kAccount;
  const primary = u.Source ?? u.source ?? null;
  // 显式空来源必须保持为空；只兼容旧 API 已明确给出的 Source。
  const sources = u.Sources ?? u.sources ?? (primary ? [primary] : []);
  const source = (sources[0] as UserSource | undefined) ?? null;
  const roles = u.Roles ?? u.roles ?? [];
  const docCategoryIds = u.DocCategoryIds ?? u.docCategoryIds ?? [];
  const studyCodes = u.StudyCodes ?? u.studyCodes ?? [];
  const region: UserRegion = u.Region ?? u.region ?? "Mainland";
  const status = u.Status ?? u.status ?? "Active";
  const lastActive = u.LastActive ?? u.lastActive;
  const createdAt = u.CreatedAt ?? u.createdAt;

  return {
    id,
    name,
    email,
    kAccount: kAccount || undefined,
    source,
    sources,
    roles,
    docCategoryIds,
    studyCodes,
    region,
    status,
    lastActive: formatDateTime(lastActive),
    createdAt: formatDate(createdAt),
  };
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
